import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: '1. Introduction',
    content: [
      'Welcome to CareersPOV, a career exploration and experiential learning platform operated by Giggly Gains, a partnership firm based in Pune, Maharashtra, India.',
      'CareersPOV is designed primarily for students in Grades 9–12, helping them explore careers, understand educational pathways and gain exposure to real-world professions through their school communities.',
      'These Terms of Use govern access to and use of the CareersPOV website, applications, dashboards and related services.',
      'By creating or using an account, users agree to comply with these Terms. Where a user is a minor, account access and participation are subject to applicable parental or legal guardian consent requirements.',
      'Parents or legal guardians who authorize a minor\'s use of CareersPOV acknowledge their responsibilities under these Terms.',
      'Visitors may browse publicly available career information without creating an account.',
    ],
  },
  {
    title: '2. Services Provided by CareersPOV',
    content: [
      'CareersPOV enables participating students, parents, schools and professional mentors to:',
    ],
    bullets: [
      'Explore careers, recommended subjects, admission requirements and educational pathways.',
      'Discover career opportunities based on school subject choices.',
      'Connect with school-approved parent professionals for career interactions and experiences.',
      'Participate in eligible workshops, mentoring sessions, job shadowing and internship opportunities.',
      'Manage experience bookings, applications, parental approvals and participation records.',
      'Receive relevant notifications, feedback and participation certificates, where applicable.',
      'Access dedicated dashboards according to their assigned roles.',
    ],
    afterBullets: [
      'Availability of individual features and opportunities may vary by participating school.',
      'CareersPOV facilitates these activities through its technology platform. Unless expressly stated otherwise, Giggly Gains does not itself provide professional career counselling, educational admissions services, employment placement or direct supervision of school-organized activities.',
    ],
  },
  {
    title: '3. User Accounts and Access',
    content: [
      'CareersPOV provides role-based accounts for students, parents, school coordinators and professional mentors.',
      'Accounts may be created through individual registration or provisioned by participating schools.',
      'Users must:',
    ],
    bullets: [
      'Provide accurate and current information.',
      'Maintain the confidentiality of their login credentials.',
      'Use only accounts and information they are authorized to access.',
      'Notify CareersPOV of suspected unauthorized account access.',
      'Comply with applicable school policies and platform requirements.',
    ],
    afterBullets: [
      'Participating schools are responsible for establishing appropriate student-parent-school relationships and verifying school affiliation through their administrative processes.',
      'School-created accounts must comply with applicable privacy, notice and consent requirements.',
      'Giggly Gains may suspend or restrict access where necessary to protect users, investigate misconduct or maintain platform security.',
    ],
  },
  {
    title: '4. Students and Parental Authorization',
    content: [
      'CareersPOV is intended for school-age students, including individuals under 18 years of age.',
      'Where required by applicable law, a parent or legal guardian must authorize the student\'s account access and processing of personal information.',
      'Students may explore available career experiences and submit booking requests through the platform.',
      'Student participation in an experience is subject to the existing parental approval process.',
      'Parental approval of an individual experience authorizes participation in that activity but does not replace any separate consent legally required for processing a child\'s personal information.',
      'Additional parental permissions and school approvals may be required for workplace visits, job shadowing and internships.',
      'Parents and guardians are encouraged to review experience details and discuss participation with their children before providing approval.',
    ],
  },
  {
    title: '5. School-Approved Professional Network',
    content: [
      'CareersPOV operates through participating school communities rather than an unrestricted public mentor marketplace.',
      'Parent professionals associated with a participating school may request registration as mentors.',
      'The relevant school coordinator reviews and approves mentor participation before mentors may offer experiences to students.',
      'Participating schools are responsible for applying appropriate verification, approval and safeguarding procedures.',
      'Students may access mentors and opportunities made available to their own school community.',
      'CareersPOV does not currently facilitate cross-school mentor sharing or student participation.',
      'School affiliation and mentor approval do not independently guarantee an individual\'s professional qualifications, suitability or conduct.',
      'CareersPOV provides technology supporting mentor applications, approvals, role-based access and school-specific visibility.',
    ],
  },
  {
    title: '6. Career Experiences and Bookings',
    content: [
      'Career experiences may include online professional interactions, workshops, workplace visits, job shadowing and eligible internships.',
      'Users must follow applicable booking, cancellation, rescheduling and participation instructions.',
      'Mentors are expected to provide accurate information about the nature, duration, location and requirements of their experiences.',
      'For online experiences:',
    ],
    bullets: [
      'Participants must use approved communication arrangements.',
      'Mentors must maintain professional and age-appropriate interactions.',
      'Mentors must not solicit unauthorized private communication with students.',
      'Students and parents must follow applicable school and platform participation guidelines.',
    ],
    afterBullets: [
      'For workplace visits:',
    ],
    bullets2: [
      'Appropriate parental authorization and school approval must be obtained.',
      'Experience details should identify the location, schedule, responsible host and relevant contact information.',
      'Suitable supervision, safety and emergency arrangements must be established.',
      'Participating schools, mentors and host organizations must comply with applicable legal and safeguarding requirements.',
    ],
    afterBullets2: [
      'Giggly Gains may restrict, cancel or suspend an experience when reasonably necessary for safety, legal compliance or platform integrity.',
    ],
  },
  {
    title: '7. Responsibilities of Participating Schools',
    content: [
      'Participating schools are responsible for managing their school communities and overseeing the activities they authorize through CareersPOV.',
      'Their responsibilities include:',
    ],
    bullets: [
      'Verifying school affiliation and reviewing mentor applications.',
      'Approving parent professionals before they offer experiences.',
      'Reviewing and overseeing opportunities offered to students.',
      'Coordinating applicable parental permissions.',
      'Establishing suitable safeguarding and supervision procedures.',
      'Managing school-related concerns and incidents.',
      'Ensuring school administrators use student information appropriately.',
    ],
    afterBullets: [
      'CareersPOV provides tools to support these responsibilities but does not replace the school\'s institutional duties.',
      'Nothing in these Terms removes any independent legal obligations applicable to Giggly Gains.',
      'Specific institutional responsibilities may also be addressed through separate agreements between Giggly Gains and participating schools.',
    ],
  },
  {
    title: '8. Responsibilities of Mentors and Host Organizations',
    content: [
      'Mentors and participating organizations must:',
    ],
    bullets: [
      'Provide truthful information about their identity, profession and experience.',
      'Conduct activities professionally, respectfully and in an age-appropriate manner.',
      'Follow applicable school requirements and safeguarding procedures.',
      'Respect student privacy and confidentiality.',
      'Avoid inappropriate, discriminatory, exploitative or unauthorized interactions.',
      'Refrain from using student information for unrelated commercial, recruitment or personal purposes.',
      'Obtain necessary approvals before arranging activities outside established platform procedures.',
      'Promptly report relevant safety concerns.',
    ],
    afterBullets: [
      'Career experiences, workplace visits and internships must be educational, age-appropriate and compliant with applicable laws.',
      'They must not involve prohibited child labour, hazardous work or exploitative activities.',
      'Mentors and host organizations are responsible for ensuring that activities they offer are legally permissible for the student\'s age and circumstances.',
    ],
  },
  {
    title: '9. Career and Educational Information',
    content: [
      'CareersPOV provides career information for educational and exploratory purposes.',
      'Career descriptions, recommended subjects, educational pathways and admission information are intended to help students and parents make informed decisions.',
      'Recommended subjects should not automatically be interpreted as mandatory admission requirements.',
      'Admission eligibility, course requirements, examination criteria and professional licensing requirements may differ between institutions and may change over time.',
      'Students and parents should independently verify current requirements with the relevant educational institution, examination authority or regulatory body before making educational decisions.',
      'CareersPOV does not guarantee:',
    ],
    bullets: [
      'Admission to any educational institution.',
      'Eligibility for a particular course or profession.',
      'Availability of a mentor, workshop or internship.',
      'Selection for an internship or employment opportunity.',
      'Any particular academic, professional or career outcome.',
    ],
  },
  {
    title: '10. Acceptable Use and Conduct',
    content: [
      'Users must not:',
    ],
    bullets: [
      'Harass, threaten, discriminate against or exploit others.',
      'Impersonate another person or provide deliberately misleading information.',
      'Attempt unauthorized access to accounts, systems or personal information.',
      'Share student information without appropriate authorization.',
      'Use the platform for unrelated commercial solicitation.',
      'Arrange unauthorized private interactions with minors.',
      'Upload unlawful, harmful or inappropriate content.',
      'Circumvent school approval, parental authorization or platform access controls.',
      'Use the platform in a manner that violates applicable laws.',
    ],
    afterBullets: [
      'Giggly Gains may investigate reported violations and restrict or suspend accounts or activities where reasonably necessary.',
    ],
  },
  {
    title: '11. Privacy and Protection of Personal Information',
    content: [
      'Giggly Gains handles personal information in accordance with its Privacy Policy and applicable Indian data protection laws.',
      'Information collected through CareersPOV may include account details, school affiliations, student-parent relationships, mentor profiles, bookings, approvals, participation records and feedback.',
      'Access to personal information is restricted according to relevant platform roles and permissions.',
      'Users must not access, disclose or misuse personal information beyond their authorized responsibilities.',
      'Where legally required, appropriate notice and consent must be obtained before processing children\'s personal information.',
      'Users may contact contact@gigglygains.com regarding privacy-related questions or requests.',
      'Further details will be provided in the CareersPOV Privacy Policy.',
    ],
  },
  {
    title: '12. Student Safety and Reporting Concerns',
    content: [
      'The safety and well-being of students are important to the operation of CareersPOV.',
      'All participating schools, parents, mentors and students must follow applicable Student Safety & Experience Guidelines.',
      'Users should promptly report concerns involving:',
    ],
    bullets: [
      'Inappropriate communication or conduct.',
      'Harassment, intimidation or discrimination.',
      'Unauthorized contact with students.',
      'Suspected exploitation or abuse.',
      'Unsafe conditions during career experiences.',
      'Misuse of student information.',
    ],
    afterBullets: [
      'Concerns relating to school-organized activities should be reported to the relevant school coordinator.',
      'Platform-related concerns may be reported to: contact@gigglygains.com',
      'Giggly Gains may take appropriate action within its responsibilities, including restricting access, preserving relevant records where lawful and cooperating with participating schools or competent authorities.',
      'Nothing in these Terms limits any mandatory reporting obligation under applicable child-protection laws.',
    ],
  },
  {
    title: '13. Third-Party Services',
    content: [
      'CareersPOV may use third-party services for features such as:',
    ],
    bullets: [
      'Video conferencing.',
      'Calendar scheduling.',
      'Email and notifications.',
      'Website hosting.',
      'Authentication and technical infrastructure.',
    ],
    afterBullets: [
      'Use of these services may also be governed by their respective terms and privacy policies.',
      'Giggly Gains is responsible for its own applicable obligations when selecting, integrating and using third-party service providers.',
    ],
  },
  {
    title: '14. Intellectual Property',
    content: [
      'The CareersPOV name, branding, original content, software, platform design and related materials are owned by Giggly Gains or used under appropriate authorization.',
      'Users may access platform materials for legitimate personal, educational and school-related purposes.',
      'Unless expressly permitted, users must not reproduce, commercially distribute, scrape, republish or exploit protected platform materials without authorization.',
      'Ownership of content supplied by schools, mentors or other third parties remains subject to applicable intellectual property rights and agreements.',
    ],
  },
  {
    title: '15. Account Suspension and Termination',
    content: [
      'Giggly Gains may suspend, restrict or terminate accounts where reasonably necessary due to:',
    ],
    bullets: [
      'Violations of these Terms.',
      'Student-safety concerns.',
      'Unauthorized access or misuse of information.',
      'Fraudulent or misleading activity.',
      'Applicable legal or regulatory requirements.',
      'Platform security or operational risks.',
    ],
    afterBullets: [
      'Where appropriate and lawful, affected users may be informed of the reason for the restriction.',
      'Account termination does not automatically require immediate deletion of information that must lawfully be retained.',
      'Users may contact Giggly Gains regarding account access or termination concerns.',
    ],
  },
  {
    title: '16. Platform Availability and Changes',
    content: [
      'Giggly Gains aims to provide reliable access to CareersPOV but does not guarantee uninterrupted or error-free operation.',
      'Features may be modified, temporarily unavailable or discontinued for maintenance, security, compliance or operational reasons.',
      'These Terms may be updated periodically.',
      'Material changes will be communicated through appropriate platform notices, email or other reasonable methods.',
      'Where legally required, additional consent or acknowledgment will be obtained.',
    ],
  },
  {
    title: '17. Limitation of Liability',
    content: [
      'Giggly Gains operates CareersPOV as a technology platform supporting career exploration and school-led professional experiences.',
      'Participating schools, mentors and host organizations remain responsible for their respective activities, decisions, representations and conduct.',
      'To the extent permitted by applicable law, Giggly Gains does not guarantee the accuracy of independently supplied information, the availability of opportunities or any particular educational or professional outcome.',
      'Giggly Gains is not responsible for independent acts or omissions of participating third parties solely by virtue of facilitating access to their activities.',
      'However, nothing in these Terms excludes or limits liability that cannot lawfully be excluded, including liability arising from Giggly Gains\' own unlawful acts, negligence or applicable statutory obligations.',
    ],
  },
  {
    title: '18. Governing Law and Jurisdiction',
    content: [
      'These Terms are governed by the laws of India.',
      'Subject to applicable law and the jurisdiction of competent courts, disputes arising from these Terms shall be subject to the courts having jurisdiction in Pune, Maharashtra, India.',
      'Nothing in this section limits statutory rights or remedies that cannot lawfully be restricted.',
    ],
  },
  {
    title: '19. Contact Information',
    content: [
      'For questions regarding these Terms, account access, platform operations or concerns relating to CareersPOV, please contact:',
    ],
    contact: true,
  },
];

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Use | CareersPOV';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Read the CareersPOV Terms of Use. Learn about platform access, student participation, school-approved mentors, parental authorization, career experiences, and user responsibilities.');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-8 sm:py-12" data-testid="terms-page">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-10 border-b border-gray-200 pb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
            Terms of Use
          </h1>
          <p className="text-sm text-[#6B7280] mb-1">
            CareersPOV &nbsp;|&nbsp; Operated by Giggly Gains
          </p>
          <p className="text-xs text-[#9CA3AF]">
            Effective Date: 9 October 2026 &nbsp;&middot;&nbsp; Last Updated: 9 October 2026
          </p>
        </header>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, idx) => (
            <section key={idx} data-testid={`terms-section-${idx + 1}`}>
              <h2 className="text-base sm:text-lg font-bold text-[#1F2937] font-['Outfit'] mb-3">
                {section.title}
              </h2>

              {section.content && (
                <div className="space-y-3">
                  {section.content.map((p, i) => (
                    <p key={i} className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                      {p.includes('contact@gigglygains.com')
                        ? p.split('contact@gigglygains.com').map((part, j, arr) => (
                            <React.Fragment key={j}>
                              {part}
                              {j < arr.length - 1 && (
                                <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
                              )}
                            </React.Fragment>
                          ))
                        : p
                      }
                    </p>
                  ))}
                </div>
              )}

              {section.bullets && (
                <ul className="mt-3 space-y-1.5 ml-1">
                  {section.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2.5 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                      <span className="mt-[10px] h-1.5 w-1.5 rounded-full bg-[#D1D5DB] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.afterBullets && (
                <div className="mt-3 space-y-3">
                  {section.afterBullets.map((p, i) => (
                    <p key={i} className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                      {p.includes('contact@gigglygains.com')
                        ? p.split('contact@gigglygains.com').map((part, j, arr) => (
                            <React.Fragment key={j}>
                              {part}
                              {j < arr.length - 1 && (
                                <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
                              )}
                            </React.Fragment>
                          ))
                        : p
                      }
                    </p>
                  ))}
                </div>
              )}

              {section.bullets2 && (
                <ul className="mt-3 space-y-1.5 ml-1">
                  {section.bullets2.map((b, i) => (
                    <li key={i} className="flex gap-2.5 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                      <span className="mt-[10px] h-1.5 w-1.5 rounded-full bg-[#D1D5DB] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.afterBullets2 && (
                <div className="mt-3 space-y-3">
                  {section.afterBullets2.map((p, i) => (
                    <p key={i} className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">{p}</p>
                  ))}
                </div>
              )}

              {section.contact && (
                <div className="mt-3 bg-gray-50 rounded-xl p-5 sm:p-6 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                  <p className="font-semibold text-[#1F2937]">Giggly Gains</p>
                  <p>Operator of CareersPOV</p>
                  <p>Indian Partnership Firm</p>
                  <p className="mt-3 font-semibold text-[#1F2937]">Registered/Business Address:</p>
                  <p>R103, Ganga Platino</p>
                  <p>Near EON IT Park, Kharadi</p>
                  <p>Pune, Maharashtra 411014</p>
                  <p>India</p>
                  <p className="mt-3">
                    Email: <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
                  </p>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-sm text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
          >
            Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
